import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Download, Camera } from "lucide-react";
import PHOTOS from "../lib/gallery.json";
import { LINKS } from "../lib/site";
import { Lightbox } from "./Lightbox";

const M = process.env.PUBLIC_URL;
const PAGE = 48;

export const CATEGORIES = [
  { key: "all", label: "All photos", match: () => true },
  { key: "pre", label: "Before the gun", match: (n) => n <= 17 },
  { key: "kids", label: "Kids 1 km Dash", match: (n) => n >= 18 && n <= 89 },
  { key: "5k", label: "5 km", match: (n) => n >= 90 && n <= 262 },
  { key: "awards", label: "Awards & finish", match: (n) => n >= 263 },
];

export const PhotoGrid = () => {
  const [cat, setCat] = useState("all");
  const [limit, setLimit] = useState(PAGE);
  const [open, setOpen] = useState(null);

  const photos = useMemo(() => PHOTOS.filter((p) => CATEGORIES.find((c) => c.key === cat).match(p.n)), [cat]);
  const visible = photos.slice(0, limit);

  const pick = (k) => { setCat(k); setLimit(PAGE); };

  return (
    <div data-testid="photo-grid-section">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap gap-2" role="tablist" data-testid="gallery-filters">
          {CATEGORIES.map((c) => {
            const count = PHOTOS.filter((p) => c.match(p.n)).length;
            const active = c.key === cat;
            return (
              <button key={c.key} role="tab" aria-selected={active} onClick={() => pick(c.key)} className={`px-4 py-2 rounded-full text-sm font-semibold border transition-all duration-200 ${active ? "bg-terracotta text-white border-terracotta shadow-warm" : "bg-white border-black/10 text-ink-soft hover:border-terracotta/50 hover:text-ink"}`} data-testid={`gallery-filter-${c.key}`}>
                {c.label} <span className={`ml-1 text-xs ${active ? "text-white/70" : "text-ink-soft/60"}`}>{count}</span>
              </button>
            );
          })}
        </div>
        <a href={LINKS.photos} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-ink text-cream text-sm font-semibold hover:bg-terracotta transition-colors duration-200" data-testid="gallery-download-all">
          <Download size={15} /> Download all originals (6.7 GB)
        </a>
      </div>

      <div className="mt-8 columns-2 sm:columns-3 lg:columns-4 gap-3 sm:gap-4 [column-fill:_balance]" data-testid="photo-grid">
        {visible.map((p, i) => (
          <motion.button
            key={p.n}
            type="button"
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "0px 0px -40px 0px" }}
            transition={{ duration: 0.4, delay: (i % 8) * 0.03 }}
            onClick={() => setOpen(photos.indexOf(p))}
            className="group relative mb-3 sm:mb-4 block w-full overflow-hidden rounded-2xl bg-cream-200 break-inside-avoid focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta"
            style={{ aspectRatio: `${p.w} / ${p.h}` }}
            data-testid={`photo-${p.n}`}
          >
            <img src={`${M}/media/gallery/thumb/${p.n}.jpg`} alt={`Grimsby Turkey Trot photo ${p.n}`} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
            <span className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <span className="pointer-events-none absolute bottom-3 left-3 inline-flex items-center gap-1.5 text-cream text-xs font-semibold opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300"><Camera size={13} /> #{p.n}</span>
          </motion.button>
        ))}
      </div>

      {limit < photos.length && (
        <div className="mt-8 text-center">
          <button onClick={() => setLimit((l) => l + PAGE)} className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full border border-ink/20 text-sm font-semibold hover:border-terracotta hover:text-terracotta transition-colors duration-200" data-testid="gallery-load-more">
            Show more photos ({photos.length - limit} left)
          </button>
        </div>
      )}

      <Lightbox photos={photos} index={open} onClose={() => setOpen(null)} onMove={setOpen} />
    </div>
  );
};
